import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';

const VenuesList = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [venueToDelete, setVenueToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Create query to get venues (only happy hours in this case)
      const venuesQuery = query(
        collection(db, 'venues'),
        where('category', '==', 'hh'),
        orderBy('headline', 'asc')
      );
      
      const querySnapshot = await getDocs(venuesQuery);
      
      const venuesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setVenues(venuesData);
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError('Failed to load venues. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const openDeleteDialog = (venue) => {
    setVenueToDelete(venue);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setVenueToDelete(null);
  };

  const handleDelete = async () => {
    if (!venueToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'venues', venueToDelete.id));
      setVenues(venues.filter(venue => venue.id !== venueToDelete.id));
      closeDeleteDialog();
    } catch (err) {
      console.error('Error deleting venue:', err);
      setError('Failed to delete venue. Please try again.');
    }
  };

  const handleEditVenue = (venueId) => {
    navigate(`/admin/venues/edit/${venueId}`);
  };

  const handleViewVenue = (venueId) => {
    window.open(`/venue/${venueId}`, '_blank');
  };

  const toggleVenueActive = async (venue) => {
    try {
      const venueRef = doc(db, 'venues', venue.id);
      await updateDoc(venueRef, {
        is_active: !venue.is_active
      });
      
      // Update local state
      setVenues(venues.map(v => 
        v.id === venue.id ? {...v, is_active: !v.is_active} : v
      ));
    } catch (err) {
      console.error('Error toggling venue active status:', err);
      setError('Failed to update venue status. Please try again.');
    }
  };

  const handleEdit = (venueId) => {
    navigate(`/admin/venues/edit/${venueId}`);
  };

  // Filter venues based on search query
  const filteredVenues = venues.filter(venue => {
    const searchIn = [
      venue.headline,
      venue.address,
      venue.main_offer
    ].join(' ').toLowerCase();
    
    return searchIn.includes(searchQuery.toLowerCase());
  });

  // Paginate venues
  const paginatedVenues = filteredVenues.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search venues by name, address, or offer..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} edge="end" size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {filteredVenues.length === 0 ? (
        <Typography align="center" sx={{ my: 4 }}>
          No venues found. Try a different search or add a new venue.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="venues table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Main Offer</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedVenues.map((venue) => (
                  <TableRow key={venue.id} hover>
                    <TableCell component="th" scope="row">
                      {venue.headline || 'No Name'}
                    </TableCell>
                    <TableCell>{venue.address || 'No Address'}</TableCell>
                    <TableCell>{venue.main_offer || 'No Offer'}</TableCell>
                    <TableCell>{venue.days || 'No Hours'}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={venue.is_active ? 'Active' : 'Inactive'} 
                        color={venue.is_active ? 'success' : 'default'}
                        onClick={() => toggleVenueActive(venue)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton 
                          color="info" 
                          onClick={() => handleViewVenue(venue.id)}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <Button
                          startIcon={<EditIcon />}
                          onClick={() => handleEdit(venue.id)}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                        </Button>
                        <IconButton 
                          color="error" 
                          onClick={() => openDeleteDialog(venue)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredVenues.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{venueToDelete?.headline}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VenuesList;